import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { formatDistanceToNow } from "date-fns";
import { ReportTableData } from "@/components/EvaluationHistory/TableView/types";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { ExternalLinkIcon } from "@heroicons/react/outline";

export const useEvaluationsTableColumns = () => {
  const columnHelper = createColumnHelper<ReportTableData>();
  const { workspaceId } = useParams();

  return [
    columnHelper.accessor("pipelineName", {
      header: "Pipeline Name",
      cell: ({ getValue }) => (
        <div className="w-full font-medium">
          <OverflowDisplayTooltip
            content={getValue() || "Unknown"}
            className="block w-full"
          >
            {getValue() || "Unknown"}
          </OverflowDisplayTooltip>
        </div>
      ),
    }),
    columnHelper.accessor("batchName", {
      header: "Batch Name",
      cell: ({ getValue }) => (
        <div className="w-full font-medium">
          <OverflowDisplayTooltip
            content={getValue() || "Unknown"}
            className="block w-full"
          >
            {getValue() || "Unknown"}
          </OverflowDisplayTooltip>
        </div>
      ),
    }),
    columnHelper.accessor("datasetName", {
      header: "Dataset Name",
      cell: (item) => {
        return (
          <Link
            className="flex items-center gap-1 underline hover:text-gray-500"
            target="_blank"
            onClick={(e) => {
              e.stopPropagation();
            }}
            to={`/workspace/${workspaceId}/dataset-groups/${item.row.original.datasetGroupId}/dataset/${item.row.original.datasetId}`}
          >
            {item.row.original.datasetName}
            <ExternalLinkIcon className="h-4 w-4" />
          </Link>
        );
      },
    }),
    columnHelper.accessor("score", {
      header: "Score",
      cell: ({ getValue }) => {
        const score = getValue();
        const validScore =
          typeof score === "number" && !isNaN(score) ? score : 0;

        return (
          <div className="flex w-full items-center">
            <span
              className={`mr-2 inline-block h-2 w-2 rounded-full ${
                validScore >= 0.8
                  ? "bg-green-500"
                  : validScore >= 0.6
                  ? "bg-yellow-500"
                  : "bg-red-500"
              }`}
            ></span>
            {validScore.toFixed(2)}
          </div>
        );
      },
    }),
    columnHelper.accessor("promptVersionNumber", {
      header: "Prompt Version",
      cell: ({ getValue }) => {
        const version = getValue();
        return (
          <div className={`w-full ${!version ? "text-gray-300" : ""}`}>
            {version ? `v${version}` : "–"}
          </div>
        );
      },
    }),
    columnHelper.accessor("createdAt", {
      header: "Date",
      cell: ({ getValue }) => {
        let date;
        try {
          date = getValue();
          if (!(date instanceof Date) || isNaN(date.getTime())) {
            date = new Date();
          }
        } catch (e) {
          date = new Date();
        }

        return (
          <div className="w-full whitespace-nowrap">
            <OverflowDisplayTooltip
              content={date.toLocaleString()}
              className="block w-full"
            >
              {formatDistanceToNow(date, {
                addSuffix: true,
              })}
            </OverflowDisplayTooltip>
          </div>
        );
      },
    }),
  ] as ColumnDef<ReportTableData>[];
};
